/**
 * MuxVideoPicker — selector reutilizable de videos de Mux para el admin.
 *
 * En vez de pegar el playback ID a mano, el admin ve sus videos de Mux con
 * thumbnail y elige. Sigue permitiendo pegar/escribir el ID a mano (fallback).
 *
 * Reutilizable: se usa en /admin/country-templates (Mux por país) y en
 * /admin/premium-invite-config (video del overlay). Lista desde
 * GET /api/admin/mux-asset-list (Clerk admin).
 *
 *   <MuxVideoPicker value={muxId} onChange={(id) => setMuxId(id)} />
 */
import { useCallback, useEffect, useState } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { isValidMuxPlaybackId } from "@/lib/internationalHomeTemplate";
import { cn } from "@/lib/utils";
import { Check, Film, Loader2, RefreshCw } from "lucide-react";

interface MuxVideo {
  playbackId: string;
  title: string | null;
  externalId: string | null;
  creatorId: string | null;
  createdAt: number | null;
  aspectRatio: string | null;
  thumbnailUrl: string;
  isBrand: boolean;
}

function thumb(playbackId: string, w = 320, h = 180): string {
  return `https://image.mux.com/${playbackId}/thumbnail.jpg?width=${w}&height=${h}&fit_mode=smartcrop&time=1`;
}

export interface MuxVideoPickerProps {
  value: string;
  onChange: (playbackId: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function MuxVideoPicker({
  value,
  onChange,
  placeholder = "Playback ID de Mux (o elegí un video)",
  className,
  disabled,
}: MuxVideoPickerProps) {
  const { getAuthHeaders } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState<MuxVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const trimmed = value.trim();
  const valid = !trimmed || isValidMuxPlaybackId(trimmed);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/mux-asset-list", { headers });
      const data = (await res.json()) as { videos?: MuxVideo[]; error?: string };
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setVideos(Array.isArray(data.videos) ? data.videos : []);
      setError(data.error ?? null);
      setLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar los videos.");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (open && !loaded) void load();
  }, [open, loaded, load]);

  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center gap-2">
        {/* Preview del video actual */}
        {trimmed && valid ? (
          <img
            src={thumb(trimmed, 96, 54)}
            alt=""
            loading="lazy"
            className="h-9 w-16 shrink-0 rounded object-cover ring-1 ring-white/10"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
            }}
          />
        ) : (
          <div className="flex h-9 w-16 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground ring-1 ring-white/10">
            <Film className="h-3.5 w-3.5 opacity-60" />
          </div>
        )}

        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "h-11 font-mono text-xs",
            trimmed && !valid && "border-destructive focus-visible:ring-destructive/40",
          )}
        />

        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-11 shrink-0"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          Elegir…
        </Button>
      </div>

      {trimmed && !valid && (
        <p className="text-[11px] text-destructive">
          Formato inválido — 8–128 caracteres (letras, números, `_-`).
        </p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span>Elegí un video de Mux</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => void load()}
                disabled={loading}
              >
                <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                Recargar
              </Button>
            </DialogTitle>
            <DialogDescription>
              Tus videos de Mux (estado “ready”). Tocá uno para asignarlo, o pegá el ID en el campo.
            </DialogDescription>
          </DialogHeader>

          {/* Paste manual dentro del modal también */}
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…o pegá el Playback ID acá"
            className="h-10 font-mono text-xs"
          />

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
              {error}
            </p>
          )}

          <div className="max-h-[55vh] overflow-y-auto pr-1">
            {loading && videos.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando videos…
              </div>
            ) : videos.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {error ? "No se pudo listar Mux." : "No hay videos en Mux para este brand."}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {videos.map((v) => {
                  const selected = v.playbackId === trimmed;
                  return (
                    <button
                      key={v.playbackId}
                      type="button"
                      onClick={() => pick(v.playbackId)}
                      className={cn(
                        "group relative overflow-hidden rounded-lg text-left ring-1 transition",
                        selected
                          ? "ring-2 ring-amber-400"
                          : "ring-white/10 hover:ring-white/30",
                      )}
                    >
                      <div className="relative aspect-video w-full bg-muted">
                        <img
                          src={v.thumbnailUrl}
                          alt={v.title ?? v.playbackId}
                          loading="lazy"
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.opacity = "0.25";
                          }}
                        />
                        {selected && (
                          <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 text-black">
                            <Check className="h-3 w-3" />
                          </span>
                        )}
                        {v.externalId && (
                          <span className="absolute left-1.5 top-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-white/80">
                            {v.externalId}
                          </span>
                        )}
                      </div>
                      <div className="px-2 py-1.5">
                        <p className="truncate text-[11px] font-medium text-foreground">
                          {v.title || "(sin título)"}
                        </p>
                        <p className="truncate font-mono text-[10px] text-muted-foreground">
                          {v.playbackId}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MuxVideoPicker;
